import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  catalogApi,
  type AssetView,
  type CategoryNode,
  type ProductDetail,
} from "../../api/catalog";
import { ordersApi } from "../../api/orders";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import Ic from "../../components/icons.jsx";
import { Select } from "../../components/Select";
import { packagingCodes, prettyAttr, prettyIndustry, skuIsCode, splitIndustry } from "./productAttrs";
import ProductDatasheet, { type DatasheetData } from "./ProductDatasheet";
import { ProductStock, useProductStock } from "./ProductStock";
import { stockStatus } from "../../lib/stockStatus";
import { catalogLocale } from "../../lib/localizedName";
import { isPieces, productUnit } from "../../lib/productUnit";

/**
 * Product detail page (PDP). Mirrors the catalog list visual language and
 * leverages the design-system classes from styles.css. The hero shows the
 * product's own image, or its category's banner when it has none; whoever may
 * edit the catalog changes that image right here.
 */
export default function CatalogProductPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const { categorySlug, sku } = useParams<{ categorySlug: string; sku: string }>();
  const { state: authState, hasPermission } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const canOrder = hasPermission("orders:create");
  const canEditCatalog = hasPermission("catalog:edit");
  // Stanje po LOT-u vide oni koji vide Magacin (prodaja, kvalitet, otkup, uprava); kupac ga ne vidi.
  const canSeeStock = hasPermission("section:warehouse:read");

  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [tree, setTree] = useState<CategoryNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  // The ERP articles a catalog product is ordered as, and the one chosen.
  const [packagings, setPackagings] = useState<ProductDetail[]>([]);
  const [packagingId, setPackagingId] = useState("");
  // An image changed on this page, until the product is loaded again.
  const [newImage, setNewImage] = useState<{ productId: string; asset: AssetView } | null>(null);

  useEffect(() => {
    if (!sku) return;
    void catalogApi.productBySku(sku).then(setProduct).catch((e: ApiError) => setError(e.message));
  }, [sku]);

  useEffect(() => {
    const codes = packagingCodes(product);
    setPackagings([]);
    if (codes.length === 0) return;
    let live = true;
    // A packaging the ERP no longer sells is gone from the catalog, so it drops out of the choice.
    void Promise.all(codes.map((c) => catalogApi.productBySku(c).catch(() => null))).then((found) => {
      if (!live) return;
      const list = found.filter((a): a is ProductDetail => a != null);
      setPackagings(list);
      setPackagingId(list[0]?.id ?? "");
    });
    return () => { live = false; };
  }, [product]);

  useEffect(() => {
    void catalogApi.tree(locale).then(setTree).catch(() => undefined);
  }, [locale]);

  const flat = useMemo(() => {
    const out: CategoryNode[] = [];
    const walk = (ns: CategoryNode[]) => ns.forEach((n) => { out.push(n); walk(n.children); });
    if (tree) walk(tree);
    return out;
  }, [tree]);

  const subCategory = useMemo(
    () => flat.find((c) => c.id === product?.categoryId) ?? null,
    [flat, product],
  );

  // Walk up to the root group for breadcrumb (e.g. BIOPRO → BIOPRO 15).
  const rootCategory = useMemo(() => {
    if (!tree) return null;
    return tree.find((root) => containsId(root, product?.categoryId)) ?? null;
  }, [tree, product]);

  // The ERP articles whose stock the page shows: the article itself, or the packagings offered in the choice.
  const stockCodes = useMemo(
    () => (!product ? [] : product.fromErp ? [product.sku] : packagings.map((a) => a.sku)),
    [product, packagings],
  );
  const stock = useProductStock(stockCodes, canSeeStock);

  if (error) return <p style={errorBox}>{error}</p>;
  if (!product) return <p style={{ color: "var(--ink-3)", padding: 20 }}>{t("catalog.pdp.loading", "Loading…")}</p>;

  const shownLocale = catalogLocale(product.translations, locale);
  const tr = shownLocale ? product.translations[shownLocale] : undefined;
  // The heading is what the article is called: an ERP article's name, or a SKU that is a name itself.
  const codeSku = skuIsCode(product.sku);
  const title = codeSku ? (tr?.name ?? product.sku) : product.sku;
  // Rich, structured product sheet carried on the product's attributes (jsonb).
  const datasheetData = (product.attributes as Record<string, unknown> | undefined)?.datasheet as
    | DatasheetData
    | undefined;
  // One PDF per variant (e.g. per granulation) when the sheet lists them, in the sheet's order.
  // Each PDF is in Serbian or English: Serbian and Croatian readers get Serbian, everyone else English.
  const pdfLang = locale === "sr" || locale === "hr" ? "sr" : "en";
  const pdfVariants = (datasheetData?.pdfVariants ?? []).filter((v) => !v.lang || v.lang === pdfLang);
  const variantIndex = (filename: string) => {
    const i = pdfVariants.findIndex((v) => v.file === filename);
    return i < 0 ? pdfVariants.length : i;
  };
  const datasheets = (product.assets ?? [])
    .filter((a) => a.kind === "DATASHEET_PDF")
    .filter((a) => !datasheetData?.pdfVariants?.length || pdfVariants.some((v) => v.file === a.filename))
    .sort((a, b) => variantIndex(a.filename) - variantIndex(b.filename));
  // Quality documents a customer may download straight from the product.
  const qualityDocs = (product.assets ?? []).filter(
    (a) => a.kind === "SPECIFICATION" || a.kind === "COA" || a.kind === "CERTIFICATE" || a.kind === "DECLARATION",
  );
  const productImage = (newImage?.productId === product.id ? newImage.asset : undefined)
    ?? product.assets?.find((a) => a.kind === "IMAGE");
  const heroUrl = subCategory?.imageUrl ?? null;

  // [i18nKey, englishDefault, value, icon] — label uses attr.* keys.
  const industryLabel = splitIndustry(attr(product, "industry")).map((v) => prettyIndustry(v, t)).join(", ");
  const headlineSpecs: Array<[string, string, string | undefined, React.ComponentType<Record<string, unknown>>]> = [
    ["attr.protein", "Protein", numWithUnit(attr(product, "protein"), "%"), Ic.trend],
    ["attr.fat", "Fat", numWithUnit(attr(product, "fat"), "%"), Ic.drop],
  ];

  // What goes on the order line: the chosen ERP packaging of a catalog product, or the article itself.
  const needsPackaging = packagingCodes(product).length > 0;
  const item = needsPackaging ? packagings.find((a) => a.id === packagingId) : product;

  // The article's own unit (e.g. "Kg"); pieces are named in the reader's language.
  const unit = productUnit(item ?? product);
  const unitLabel = isPieces(unit) ? t("catalog.pdp.unit.pcs", "pcs") : unit;

  async function handleAddToOrder() {
    if (!item) return;
    if (!canOrder) {
      toast.error(t("catalog.pdp.error.noPermission", "Your role can't create orders."));
      return;
    }
    if (authState.status !== "authenticated" || !authState.user.companyId) {
      toast.error(t("catalog.pdp.error.noCompany", "You're not bound to a company."));
      return;
    }
    setBusy(true);
    try {
      const drafts = await ordersApi.list({
        status: "DRAFT",
        companyId: authState.user.companyId ?? undefined,
        size: 1,
      });
      const myDraft = drafts.content[0];
      if (myDraft) {
        await ordersApi.addLine(myDraft.id, { productId: item.id, quantity: qty, unit });
        toast.success(t("catalog.pdp.success.addedToExisting", "Added {{qty}}× {{sku}} to draft {{orderNumber}}.", {
          qty,
          sku: item.sku,
          orderNumber: myDraft.orderNumber,
        }));
      } else {
        const created = await ordersApi.create({
          companyId: authState.user.companyId,
          lines: [{ productId: item.id, quantity: qty, unit }],
        });
        toast.success(t("catalog.pdp.success.created", "Created draft {{orderNumber}}.", { orderNumber: created.orderNumber }));
        navigate(`/orders/${created.id}`);
      }
    } catch (e) {
      toast.error((e as ApiError).message ?? t("catalog.pdp.error.couldNotAdd", "Could not add to order."));
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload(asset: AssetView) {
    if (!product) return;
    try {
      const r = await catalogApi.presignAsset(product.id, asset.id);
      window.open(r.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error((e as ApiError).message ?? t("catalog.pdp.error.couldNotOpenFile", "Could not open file."));
    }
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <nav className="crumbs" aria-label={t("catalog.pdp.breadcrumb.label", "Breadcrumb")}>
        <span style={crumbMuted}>{t("catalog.pdp.breadcrumb.products", "Products")}</span>
        <span><Link to="/catalog" style={crumbLink}>{t("catalog.pdp.breadcrumb.catalog", "Catalog")}</Link></span>
        {/* Flat catalogs: the product's own category is the root, so it is shown once. */}
        {rootCategory && rootCategory.id !== subCategory?.id && (
          <span><Link to="/catalog" style={crumbLink}>{rootCategory.name}</Link></span>
        )}
        {subCategory && (
          <span><Link to={`/catalog/${categorySlug}`} style={crumbLink}>{subCategory.name}</Link></span>
        )}
        <span style={crumbCurrent}>{title}</span>
      </nav>

      <div className="pdp-grid">
        {/* LEFT: hero + actions */}
        <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={hero}>
            {productImage || heroUrl ? (
              productImage ? (
                <ImageAsset productId={product.id} asset={productImage} />
              ) : (
                <img
                  src={heroUrl ?? undefined}
                  alt={product.sku}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              )
            ) : (
              <span style={{ color: "var(--ink-4)" }}>{t("catalog.pdp.noImage", "No image")}</span>
            )}
          </div>
          {canEditCatalog && (
            <ChangeImage
              productId={product.id}
              hasImage={!!productImage}
              onChanged={(asset) => setNewImage({ productId: product.id, asset })}
            />
          )}

          {needsPackaging && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={packagingLabel}>{t("catalog.pdp.packaging.label", "Packaging")}</span>
              <Select
                value={packagingId}
                options={packagings.map((a) => ({ value: a.id, label: `${articleName(a, locale)} · ${a.sku}` }))}
                onChange={setPackagingId}
                disabled={packagings.length === 0}
                placeholder={t("catalog.pdp.loading", "Loading…")}
                ariaLabel={t("catalog.pdp.packaging.label", "Packaging")}
              />
            </div>
          )}

          <div style={actionRow}>
            <div style={qtyBox} role="group" aria-label={t("catalog.pdp.quantity.label", "Quantity")}>
              <button
                type="button"
                onClick={() => setQty((n) => Math.max(1, n - 1))}
                style={qtyBtn}
                aria-label={t("catalog.pdp.quantity.decrease", "Decrease")}
              >
                <span style={{ fontSize: 16, fontWeight: 500 }}>−</span>
              </button>
              <input
                value={qty}
                onChange={(e) => {
                  const n = parseInt(e.target.value.replace(/[^0-9]/g, ""), 10);
                  setQty(Number.isFinite(n) && n > 0 ? n : 1);
                }}
                style={qtyInput}
                inputMode="numeric"
                aria-label={t("catalog.pdp.quantity.input", "Quantity")}
              />
              <button
                type="button"
                onClick={() => setQty((n) => n + 1)}
                style={{ ...qtyBtn, borderRight: "none" }}
                aria-label={t("catalog.pdp.quantity.increase", "Increase")}
              >
                <span style={{ fontSize: 16, fontWeight: 500 }}>+</span>
              </button>
              <span style={qtyUnit}>{unitLabel}</span>
            </div>
            <button
              type="button"
              className="btn primary"
              onClick={handleAddToOrder}
              disabled={busy || !canOrder || !item}
              style={{ flex: 1, height: 44 }}
              title={canOrder
                ? t("catalog.pdp.addToOrder.title", "Add to current draft order")
                : t("catalog.pdp.addToOrder.noPermission", "Your role can't create orders")}
            >
              <Ic.plus /> {busy
                ? t("catalog.pdp.addToOrder.loading", "Adding…")
                : qty > 1
                  ? t("catalog.pdp.addToOrder.buttonPlural", "Add {{qty}} {{unit}} to order", { qty, unit: unitLabel })
                  : t("catalog.pdp.addToOrder.buttonSingular", "Add to order")}
            </button>
          </div>

          {canSeeStock && item && stockCodes.includes(item.sku) && (
            <ProductStock
              key={item.sku}
              state={stock}
              article={item.sku}
              packagings={needsPackaging ? packagings.map((a) => a.sku) : undefined}
              onPick={
                needsPackaging
                  ? (code) => setPackagingId(packagings.find((a) => a.sku === code)?.id ?? packagingId)
                  : undefined
              }
            />
          )}

          {datasheets.length > 0 ? (
            datasheets.map((doc) => {
              const size = (doc.sizeBytes / 1024).toFixed(0);
              const variant = pdfVariants.find((v) => v.file === doc.filename)?.label;
              return (
                <button key={doc.id} type="button" className="btn" onClick={() => handleDownload(doc)} style={{ width: "100%" }}>
                  <Ic.pdf />{" "}
                  {variant
                    ? t("catalog.pdp.datasheet.downloadVariant", "Download datasheet · {{variant}} · {{size}} KB", { variant, size })
                    : t("catalog.pdp.datasheet.download", "Download datasheet · {{size}} KB", { size })}
                </button>
              );
            })
          ) : (
            <button
              type="button"
              className="btn"
              style={{ width: "100%", color: "var(--ink-4)" }}
              onClick={() => toast.info(t("catalog.pdp.datasheet.info", "Datasheet PDF lands with the ERP integration."))}
            >
              <Ic.pdf /> {t("catalog.pdp.datasheet.comingSoon", "Datasheet — coming soon")}
            </button>
          )}

          {qualityDocs.map((doc) => (
            <button
              key={doc.id}
              type="button"
              className="btn"
              onClick={() => handleDownload(doc)}
              style={{ width: "100%" }}
            >
              <Ic.pdf /> {t(`catalog.pdp.doc.${doc.kind}`, doc.filename)}
            </button>
          ))}
        </section>

        {/* RIGHT: title + key specs + description */}
        <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <header>
            <h1 style={{ margin: 0, fontSize: 28, color: "var(--brand-primary)", letterSpacing: "-0.01em" }}>
              {title}
            </h1>
            {codeSku ? (
              <div style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4 }}>
                <code style={mono}>{product.sku}</code>
                {subCategory && <> · {subCategory.name}</>}
              </div>
            ) : (
              <>
                {tr?.name && (
                  <div style={{ color: "var(--ink-2)", fontSize: 15, marginTop: 4 }}>{tr.name}</div>
                )}
                {subCategory && (
                  <div style={{ color: "var(--ink-3)", fontSize: 12.5, marginTop: 3 }}>{subCategory.name}</div>
                )}
              </>
            )}
          </header>

          {(() => {
            // Unknown stock draws no pill: "out of stock" would be a claim nobody measured.
            const status = stockStatus(product.sku);
            if (status === "UNKNOWN") return null;
            const inStock = status === "IN";
            return (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 7, width: "fit-content",
                fontSize: 13, fontWeight: 600, padding: "5px 13px", borderRadius: 999,
                color: inStock ? "var(--ok)" : "var(--err)",
                background: inStock ? "var(--ok-bg)" : "var(--err-bg)",
              }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: inStock ? "var(--ok)" : "var(--err)", flex: "none" }} />
                {inStock ? t("catalog.inStock", "In stock") : t("catalog.outOfStock", "Out of stock")}
              </span>
            );
          })()}

          {shownLocale === "en" && locale !== "en" && (
            <div style={banner}>{t("catalog.pdp.translationFallback", "Translation not available in {{locale}} — showing English.", { locale: locale.toUpperCase() })}</div>
          )}
          {shownLocale === "sr" && locale !== "sr" && (
            <div style={banner}>{t("catalog.pdp.translationFallbackSr", "Translation not available in {{locale}} — showing Serbian.", { locale: locale.toUpperCase() })}</div>
          )}

          {tr?.shortDescription && (
            <p style={{ margin: 0, fontSize: 15, color: "var(--ink-2)" }}>{tr.shortDescription}</p>
          )}

          <div style={kpiGrid}>
            {headlineSpecs.filter(([, , v]) => v).map(([labelKey, labelDefault, value, Icon]) => (
              <div key={labelKey} style={kpiBox}>
                <div style={kpiHeader}>
                  <span style={kpiIcon}><Icon /></span>
                  <span style={kpiLabel}>{t(labelKey, labelDefault)}</span>
                </div>
                <strong style={kpiValue}>{value}</strong>
              </div>
            ))}
          </div>

          {industryLabel && (
            <div>
              <div style={kpiLabel}>{t("attr.industry", "Industry")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {splitIndustry(attr(product, "industry")).map((slug) => (
                  <span key={slug} style={industryChip}>{prettyIndustry(slug, t)}</span>
                ))}
              </div>
            </div>
          )}

          {datasheetData ? (
            <ProductDatasheet data={datasheetData} />
          ) : (
            <>
              <h2 style={sectionTitle}>{t("catalog.pdp.fullSpecs", "Full specifications")}</h2>
              <table style={specTable}>
                <tbody>
                  {Object.entries(product.attributes ?? {})
                    .filter(([k]) => k !== "datasheet")
                    .map(([k, v]) => (
                      <tr key={k}>
                        <td style={specKey}>{prettyAttr(k, t)}</td>
                        <td style={specVal}>{String(v)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}

          {tr?.longDescription && (
            <>
              <h2 style={sectionTitle}>{t("catalog.pdp.description", "Description")}</h2>
              <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: "var(--ink-2)" }}>{tr.longDescription}</p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

// ---- helpers --------------------------------------------------------------

function ImageAsset({ productId, asset }: { productId: string; asset: AssetView }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    void catalogApi.presignAsset(productId, asset.id).then((r) => setUrl(r.url)).catch(() => setUrl(null));
  }, [productId, asset.id]);
  if (!url) return <span style={{ color: "var(--ink-4)" }}>{t("catalog.pdp.loadingImage", "Loading image…")}</span>;
  return (
    <img
      src={url}
      alt={asset.filename}
      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
    />
  );
}

/**
 * The new image takes the place of the old one (the server keeps one per product). A square of about
 * 1200 × 1200 px fills the frame on the card and on this page, sharp on every screen.
 */
function ChangeImage({ productId, hasImage, onChanged }: {
  productId: string;
  hasImage: boolean;
  onChanged: (asset: AssetView) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A real button opens the file picker, so the keyboard reaches it too.
  const picker = useRef<HTMLInputElement>(null);

  async function pick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      onChanged(await catalogApi.uploadAsset(productId, "IMAGE", file));
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <button type="button" className="btn sm" disabled={busy} onClick={() => picker.current?.click()}>
        <Ic.upload />{" "}
        {busy
          ? t("catalog.pdp.imageUploading", "Uploading…")
          : hasImage ? t("catalog.pdp.changeImage", "Change image") : t("catalog.pdp.addImage", "Add image")}
      </button>
      <input ref={picker} type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} hidden />
      <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{t("catalog.pdp.imageHint", "Square image, about 1200 × 1200 px")}</span>
      {error && <span role="alert" style={{ ...errorBox, padding: "4px 8px", fontSize: 12 }}>{error}</span>}
    </div>
  );
}

function attr(p: ProductDetail, key: string): string {
  const v = (p.attributes as Record<string, unknown> | undefined)?.[key];
  return v == null ? "" : String(v);
}

function numWithUnit(raw: string, unit: string): string | undefined {
  if (!raw) return undefined;
  return raw.includes(unit.trim()) ? raw : `${raw}${unit}`;
}

/** An article's name in the reader's language, else English, else Serbian, else its code. */
function articleName(a: ProductDetail, locale: string): string {
  const l = catalogLocale(a.translations, locale);
  return (l && a.translations[l]?.name) || a.sku;
}


function containsId(node: CategoryNode, id: string | null | undefined): boolean {
  if (!id) return false;
  if (node.id === id) return true;
  return node.children.some((c) => containsId(c, id));
}

// ---- styles ---------------------------------------------------------------

const hero: React.CSSProperties = {
  width: "100%",
  aspectRatio: "1 / 1",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  display: "grid",
  placeItems: "center",
  overflow: "hidden",
  padding: 20,
};
const actionRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};
const qtyBox: React.CSSProperties = {
  display: "flex",
  alignItems: "stretch",
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)",
  overflow: "hidden",
  height: 44,
};
const qtyBtn: React.CSSProperties = {
  width: 36,
  height: "100%",
  display: "grid",
  placeItems: "center",
  background: "transparent",
  border: "none",
  borderRight: "1px solid var(--border)",
  cursor: "pointer",
  color: "var(--ink-2)",
  padding: 0,
};
const qtyInput: React.CSSProperties = {
  width: 56,
  textAlign: "center",
  border: "none",
  borderRight: "1px solid var(--border)",
  background: "transparent",
  fontSize: 14,
  fontWeight: 600,
  fontVariantNumeric: "tabular-nums",
  outline: "none",
  color: "var(--ink)",
};
const kpiGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: 10,
  alignItems: "start",
};
const industryChip: React.CSSProperties = {
  fontSize: 12,
  color: "var(--brand-ink)",
  background: "var(--brand-primary-50)",
  border: "1px solid var(--border)",
  padding: "4px 11px",
  borderRadius: 999,
};
const kpiBox: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)",
  padding: "10px 12px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};
const kpiHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
};
const kpiIcon: React.CSSProperties = {
  display: "inline-grid",
  placeItems: "center",
  width: 18,
  height: 18,
  color: "var(--brand-primary)",
  opacity: 0.75,
};
const kpiLabel: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: "var(--ink-3)",
};
const packagingLabel: React.CSSProperties = { ...kpiLabel, fontSize: 11 };
const qtyUnit: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "0 14px",
  background: "var(--surface-2)",
  fontSize: 12.5,
  color: "var(--ink-3)",
  borderLeft: "1px solid var(--border)",
};
const kpiValue: React.CSSProperties = { fontSize: 24, fontWeight: 700, color: "var(--brand-ink)", letterSpacing: "-0.01em" };
const sectionTitle: React.CSSProperties = {
  margin: "8px 0 0",
  fontSize: 11,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: "var(--ink-3)",
};
const specTable: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)",
  overflow: "hidden",
};
const specKey: React.CSSProperties = {
  padding: "8px 12px",
  color: "var(--ink-3)",
  borderBottom: "1px solid var(--border)",
  width: "44%",
};
const specVal: React.CSSProperties = {
  padding: "8px 12px",
  color: "var(--ink)",
  borderBottom: "1px solid var(--border)",
};
const banner: React.CSSProperties = {
  padding: 8,
  background: "var(--warn-bg)",
  color: "var(--warn)",
  borderRadius: "var(--r-sm)",
  fontSize: 12,
};
const errorBox: React.CSSProperties = {
  padding: 10,
  background: "var(--err-bg)",
  color: "var(--err)",
  borderRadius: 6,
  fontSize: 13,
};
const crumbMuted: React.CSSProperties = { color: "var(--ink-4)" };
const crumbLink: React.CSSProperties = { color: "var(--ink-3)", textDecoration: "none" };
const mono: React.CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: 12,
  color: "var(--ink-3)",
};
const crumbCurrent: React.CSSProperties = { color: "var(--ink-2)", fontWeight: 600 };
